import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle, Edit } from 'lucide-react';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useUser } from '@/contexts/UserContext';

export const ProfileCompletionPrompt: React.FC = () => {
  const navigate = useNavigate();
  const { accountType } = useUser();
  const { completionStatus, loading } = useProfileCompletion();

  if (loading || completionStatus.isComplete) {
    return null;
  }

  const handleCompleteProfile = () => {
    navigate('/profile/complete');
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCompletionIcon = (percentage: number) => {
    if (percentage >= 80) return <CheckCircle className="h-5 w-5 text-green-600" />;
    return <AlertCircle className="h-5 w-5 text-yellow-600" />;
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {getCompletionIcon(completionStatus.percentage)}
          Profile Completion
          <Badge variant="outline" className={getCompletionColor(completionStatus.percentage)}>
            {completionStatus.percentage}%
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress value={completionStatus.percentage} className="w-full" />
        
        {completionStatus.missingFields.length > 0 && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              Missing information:
            </p>
            <div className="flex flex-wrap gap-2">
              {completionStatus.missingFields.map((field, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {field}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Stack vertically on mobile (button below text). On sm+ keep them on
            one row but constrain button width so it doesn't sprawl, and don't
            shrink the helper text more than it needs to. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground sm:flex-1">
            Complete your profile to unlock all features and improve your visibility.
          </p>
          <Button onClick={handleCompleteProfile} className="w-full sm:w-auto sm:min-w-[180px] flex-shrink-0">
            <Edit className="h-4 w-4 mr-2" />
            Complete Profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};